/**
 * Jewellery you own (Jumbo's Jewellery & beauty, the jeweller in town): kept in `owned` as jewel:<id>. What you picked
 * when your life began is yours too (the game adds it). The pieces themselves (looks, slots) live with the body,
 * in the game; here: owning, and paying for, them.
 */
import { spend, type LifeState } from './life.ts';
import { localPrice } from './character.ts';
import { deliveryAddress, onItsWay, placeOrder } from './orders.ts';

export const jewelKey = (id: string) => `jewel:${id}`;
export const ownsJewel = (s: LifeState, id: string) => s.owned.includes(jewelKey(id));
/** a piece's price where you are (naira in Lagos, the local money abroad) */
export const jewelPrice = (s: LifeState, naira: number) => localPrice(s, naira, 500);
export function buyJewel(s: LifeState, id: string, label: string, naira: number, from = 'Jumbo'): { ok: boolean; line: string } {
  if (ownsJewel(s, id)) return { ok: false, line: 'You already have it.' };
  if (from === 'Jumbo') return jumboOrder(s, jewelKey(id), label, naira);
  if (!spend(s, jewelPrice(s, naira), `${from}: ${label}`)) return { ok: false, line: 'You can’t afford it.' };
  s.owned.push(jewelKey(id));
  return { ok: true, line: `${label}: yours. They wrap it for you.` };
}
export const hatKey = (id: string) => `hat:${id}`;
export const ownsHat = (s: LifeState, id: string) => s.owned.includes(hatKey(id));
/** a hat (caps, beanies, bucket hats, durags): Jumbo's rider brings it, or a boutique bags it */
export function buyHat(s: LifeState, id: string, label: string, naira: number, from = 'Jumbo'): { ok: boolean; line: string } {
  if (ownsHat(s, id)) return { ok: false, line: 'You already have it.' };
  if (from === 'Jumbo') return jumboOrder(s, hatKey(id), label, naira);
  if (!spend(s, jewelPrice(s, naira), `${from}: ${label}`)) return { ok: false, line: 'You can’t afford it.' };
  s.owned.push(hatKey(id));
  return { ok: true, line: `${label}: yours.` };
}
export const outfitKey = (id: string) => `outfit:${id}`;
export const ownsOutfit = (s: LifeState, id: string) => s.owned.includes(outfitKey(id));
/** clothes from Jumbo: the rider brings them, they go in your wardrobe (and on from Owned, wherever you are) */
export function buyOutfit(s: LifeState, id: string, label: string, naira: number): { ok: boolean; line: string } {
  if (ownsOutfit(s, id)) return { ok: false, line: 'You already have it.' };
  return jumboOrder(s, outfitKey(id), label, naira);
}
/** a Jumbo order: paid now, on a rider to where you're staying (see orders.ts) */
export function jumboOrder(s: LifeState, key: string, label: string, naira: number): { ok: boolean; line: string } {
  if (onItsWay(s, key)) return { ok: false, line: 'It’s already on its way: see Your orders.' };
  if (!deliveryAddress(s)) return { ok: false, line: 'Jumbo needs an address here: book a hotel room, or get a place, and they’ll bring it there.' };
  if (!spend(s, jewelPrice(s, naira), `Jumbo: ${label}`)) return { ok: false, line: 'You can’t afford it.' };
  const o = placeOrder(s, key, label)!;
  return { ok: true, line: `${label}: ordered. A rider brings it to ${o.to === 'hotel' ? `your hotel (${o.place})` : 'your door'}: about ${Math.round((o.eta - s.minutes) / 5) * 5} minutes. Track it in Your orders.` };
}
/** the jewellery a new life starts with (what was picked in the creator) */
export function giveJewels(s: LifeState, ids: string[]) { for (const id of ids) if (!ownsJewel(s, id)) s.owned.push(jewelKey(id)); }
